/**
 * Notifications hub: manage user preferences (email/phone/timezone), browse
 * existing subscriptions, edit/disable/delete them, and inspect recent
 * deliveries. Phone verification round-trip lives here too.
 */
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { notificationsApi } from '../lib/notifications-api';
import { isAuthPending } from '../lib/api';
import { useIsAdmin } from '../lib/auth';
import { confirmDialog, toast } from '../lib/dialogs';
import { LoadingBlock, Spinner } from '../components/Spinner';
import AdminRecipientsSection from '../components/AdminRecipientsSection';
import type {
  Subscription,
  SubscriptionDelivery,
  UserPreferences
} from '../types';

export default function NotificationsPage() {
  const isAdmin = useIsAdmin();
  const [prefs, setPrefs] = useState<UserPreferences | null>(null);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const [p, s] = await Promise.all([
        notificationsApi.getPreferences(),
        notificationsApi.list()
      ]);
      setPrefs(p);
      setSubs(s);
    } catch (e: any) {
      if (!isAuthPending(e)) setError(e?.message ?? 'Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, []);

  return (
    <div>
      <h1 className="h3 mb-4">Notifications</h1>

      {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}

      <section aria-labelledby="prefs-heading" className="mb-4">
        <h2 id="prefs-heading" className="h5">Your preferences</h2>
        {prefs ? <PreferencesPanel prefs={prefs} onChange={setPrefs} /> : loading ? <LoadingBlock label="Loading preferences…" /> : null}
      </section>

      <section aria-labelledby="subs-heading" className="mb-4">
        <h2 id="subs-heading" className="h5">My subscriptions ({subs.length})</h2>
        {loading ? (
          <LoadingBlock label="Loading subscriptions…" />
        ) : subs.length === 0 ? (
          <p className="text-muted">
            No subscriptions yet. Open an event, venue, or series page and tap <strong>🔔 Subscribe</strong> to start.
          </p>
        ) : (
          <SubscriptionsTable subs={subs} onChange={reload} />
        )}
      </section>

      {isAdmin ? <AdminRecipientsSection /> : null}

      <p className="small text-muted">
        SMS messages may incur standard text rates. Reply <code>STOP</code> to any text to opt out, or use the
        unsubscribe link in any email. <Link to="/notifications">Refresh</Link>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preferences panel — email/timezone/quiet hours + phone verification
// ---------------------------------------------------------------------------

interface PrefsProps {
  prefs: UserPreferences;
  onChange: (next: UserPreferences) => void;
}

function PreferencesPanel({ prefs, onChange }: PrefsProps) {
  const [email, setEmail] = useState(prefs.email ?? '');
  const [timezone, setTimezone] = useState(prefs.timezone || 'America/New_York');
  const [quietStart, setQuietStart] = useState(prefs.quietHoursStart ?? '');
  const [quietEnd, setQuietEnd] = useState(prefs.quietHoursEnd ?? '');
  const [emailOptOut, setEmailOptOut] = useState(!!prefs.emailOptOut);
  const [smsOptOut, setSmsOptOut] = useState(!!prefs.smsOptOut);
  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState(prefs.weeklyDigestEnabled === true);
  const [testingDigest, setTestingDigest] = useState(false);
  const [digestTestQueued, setDigestTestQueued] = useState(false);
  const [savedEventNotify, setSavedEventNotify] = useState(prefs.savedEventNotify !== false);
  const [savedEventChannel, setSavedEventChannel] = useState<'auto' | 'sms' | 'email'>(prefs.savedEventChannel ?? 'auto');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const next = await notificationsApi.updatePreferences({
        email: email.trim() || undefined,
        timezone,
        quietHoursStart: quietStart || undefined,
        quietHoursEnd: quietEnd || undefined,
        emailOptOut,
        smsOptOut,
        weeklyDigestEnabled,
        savedEventNotify,
        savedEventChannel
      });
      onChange(next);
      setSavedAt(Date.now());
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  }

  async function sendDigestTest() {
    setTestingDigest(true);
    setDigestTestQueued(false);
    setErr(null);
    try {
      await notificationsApi.testWeeklyDigest();
      setDigestTestQueued(true);
    } catch (error) {
      if (!isAuthPending(error)) setErr(error instanceof Error ? error.message : 'Could not queue the test email.');
    } finally {
      setTestingDigest(false);
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="row g-3">
          <div className="col-md-6">
            <label htmlFor="pref-email" className="form-label">Email address</label>
            <input id="pref-email" type="email" className="form-control" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="col-md-6">
            <label htmlFor="pref-tz" className="form-label">Timezone</label>
            <input id="pref-tz" className="form-control" value={timezone} onChange={e => setTimezone(e.target.value)} />
            <div className="form-text">e.g. <code>America/New_York</code>. Used for day-of-week and time-of-day filters.</div>
          </div>
          <div className="col-md-3">
            <label htmlFor="pref-q-start" className="form-label">Quiet hours start</label>
            <input id="pref-q-start" type="time" className="form-control" value={quietStart} onChange={e => setQuietStart(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label htmlFor="pref-q-end" className="form-label">Quiet hours end</label>
            <input id="pref-q-end" type="time" className="form-control" value={quietEnd} onChange={e => setQuietEnd(e.target.value)} />
          </div>
          <div className="col-md-6 d-flex align-items-end">
            <div>
              <div className="form-check">
                <input type="checkbox" className="form-check-input" id="pref-email-opt" checked={emailOptOut} onChange={e => setEmailOptOut(e.target.checked)} />
                <label htmlFor="pref-email-opt" className="form-check-label">Pause all email</label>
              </div>
              <div className="form-check">
                <input type="checkbox" className="form-check-input" id="pref-sms-opt" checked={smsOptOut} onChange={e => setSmsOptOut(e.target.checked)} />
                <label htmlFor="pref-sms-opt" className="form-check-label">Pause all SMS</label>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4" aria-labelledby="weekly-digest-heading">
          <h3 id="weekly-digest-heading" className="h6 mb-1">Weekly event digest</h3>
          <p className="small text-muted mb-2">
            Thursdays at <strong>7 a.m.</strong>
            Get up to 15 events for Thursday through Wednesday, prioritizing your saved events
            and the types of events you like, using your taste settings. Followed venues and
            series only break ties or fill remaining slots. No personal matches? We&apos;ll send
            a few general local picks instead.
          </p>
          <div className="form-check form-switch mb-2">
            <input
              type="checkbox"
              className="form-check-input"
              role="switch"
              id="pref-weekly-digest"
              checked={weeklyDigestEnabled}
              onChange={e => setWeeklyDigestEnabled(e.target.checked)}
            />
            <label htmlFor="pref-weekly-digest" className="form-check-label">
              Email me the weekly event digest
            </label>
          </div>
          <p className="small text-muted mb-2">
            Save and Not interested links update your account automatically after opening the site.
            Each event also has View event and Add to calendar links. &ldquo;Pause all email&rdquo; overrides this setting.
          </p>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => void sendDigestTest()}
            disabled={testingDigest || digestTestQueued || saving || !weeklyDigestEnabled
              || !prefs.weeklyDigestEnabled || emailOptOut || !!prefs.emailOptOut
              || email.trim() !== prefs.email}
          >
            {testingDigest ? 'Queuing test…' : 'Send me a test digest'}
          </button>
          <span className="small text-muted ms-2">
            Save your preferences first. Tests do not replace Thursday&apos;s email.
          </span>
          {digestTestQueued ? <p className="small text-success mt-2 mb-0" role="status">Test digest queued. Check your email.</p> : null}
        </div>
        <div className="mt-4">
          <h3 className="h6 mb-1">Saved-event reminders</h3>
          <p className="small text-muted mb-2">
            When you save (👍) an event, we can text or email you the day before with all your
            saved events happening the next day — one message, no spam.
          </p>
          <div className="form-check form-switch mb-2">
            <input
              type="checkbox"
              className="form-check-input"
              role="switch"
              id="pref-saved-notify"
              checked={savedEventNotify}
              onChange={e => setSavedEventNotify(e.target.checked)}
            />
            <label htmlFor="pref-saved-notify" className="form-check-label">
              Remind me the day before about events I've saved
            </label>
          </div>
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label htmlFor="pref-saved-channel" className="form-label">Send via</label>
              <select
                id="pref-saved-channel"
                className="form-select"
                value={savedEventChannel}
                onChange={e => setSavedEventChannel(e.target.value as 'auto' | 'sms' | 'email')}
                disabled={!savedEventNotify}
              >
                <option value="auto">Text if my phone is verified, otherwise email</option>
                <option value="sms">Text only</option>
                <option value="email">Email only</option>
              </select>
              <div className="form-text">
                Text needs a verified phone below. Otherwise we'll email {email.trim() || 'your address'}.
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3 d-flex gap-2 align-items-center">
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          {savedAt ? <span className="text-success small" role="status">Saved.</span> : null}
          {err ? <span className="text-danger small" role="alert">{err}</span> : null}
        </div>

        <hr />

        <PhoneVerifySection prefs={prefs} onChange={onChange} />
      </div>
    </div>
  );
}

function PhoneVerifySection({ prefs, onChange }: PrefsProps) {
  const [phone, setPhone] = useState(prefs.phone ?? '');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function startVerify() {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await notificationsApi.startPhoneVerify(phone.trim());
      setSentTo(phone.trim());
      setMsg('Code sent. Check your text messages.');
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to send code. Make sure the number is in E.164 format (e.g. +19045551234).');
    } finally { setBusy(false); }
  }

  async function confirm() {
    if (!sentTo) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const next = await notificationsApi.confirmPhoneVerify(sentTo, code.trim());
      onChange(next);
      setMsg('Phone verified.');
      setCode('');
    } catch (e: any) {
      setErr(e?.message ?? 'Verification failed.');
    } finally { setBusy(false); }
  }

  async function removePhone() {
    if (!(await confirmDialog({
      title: 'Remove phone',
      body: 'Remove your phone number?',
      confirmLabel: 'Remove',
      danger: true,
    }))) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await notificationsApi.deletePhone();
      const fresh = await notificationsApi.getPreferences();
      onChange(fresh);
      setPhone(''); setSentTo(null); setMsg('Phone removed.');
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to remove phone.');
    } finally { setBusy(false); }
  }

  const verified = !!prefs.phoneVerifiedAt && prefs.phone === phone;

  return (
    <div>
      <h3 className="h6">Phone for SMS</h3>
      <p className="small text-muted mb-2">
        We'll send a 6-digit code to verify your number. Standard text rates apply.
      </p>
      <div className="row g-2 align-items-end">
        <div className="col-md-5">
          <label htmlFor="pref-phone" className="form-label">Phone (E.164)</label>
          <input id="pref-phone" className="form-control" placeholder="+19045551234" value={phone} onChange={e => setPhone(e.target.value)} />
        </div>
        <div className="col-md-3">
          <button type="button" className="btn btn-outline-primary" onClick={startVerify} disabled={busy || !phone.trim()}>
            {sentTo ? 'Resend code' : 'Send code'}
          </button>
        </div>
        {prefs.phone ? (
          <div className="col-md-2">
            <button type="button" className="btn btn-outline-danger" onClick={removePhone} disabled={busy}>Remove</button>
          </div>
        ) : null}
        <div className="col-md-12">
          {verified ? <span className="badge bg-success">Verified</span> : null}
        </div>
        {sentTo ? (
          <>
            <div className="col-md-3">
              <label htmlFor="pref-code" className="form-label">Verification code</label>
              <input id="pref-code" inputMode="numeric" pattern="[0-9]*" maxLength={6} className="form-control" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
            </div>
            <div className="col-md-3">
              <button type="button" className="btn btn-primary" onClick={confirm} disabled={busy || code.length !== 6}>Confirm</button>
            </div>
          </>
        ) : null}
      </div>
      {msg ? <div className="text-success small mt-2" role="status">{msg}</div> : null}
      {err ? <div className="text-danger small mt-2" role="alert">{err}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subscriptions table
// ---------------------------------------------------------------------------

interface TableProps {
  subs: Subscription[];
  onChange: () => void | Promise<void>;
}

/** Format reminder offsets for display in the subscriptions table. */
function formatReminderOffsets(s: Subscription): string {
  const offsets = s.reminderOffsets && s.reminderOffsets.length > 0
    ? s.reminderOffsets
    : (s.reminderDayBefore ? [24] : []);
  if (offsets.length === 0) return '—';
  return offsets
    .map(h => h === 24 ? '1d' : h === 48 ? '2d' : h === 168 ? '1w' : h % 24 === 0 ? `${h / 24}d` : `${h}h`)
    .join(', ') + ' before';
}

function SubscriptionsTable({ subs, onChange }: TableProps) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openDeliveriesId, setOpenDeliveriesId] = useState<string | null>(null);

  async function toggleActive(s: Subscription) {
    setBusyId(s.id);
    try {
      await notificationsApi.update(s.id, { isActive: !s.isActive });
      await onChange();
    } finally { setBusyId(null); }
  }

  async function remove(s: Subscription) {
    if (!(await confirmDialog({
      title: 'Delete subscription',
      body: `Delete subscription "${s.name}"?`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    setBusyId(s.id);
    try {
      await notificationsApi.remove(s.id, true);
      await onChange();
    } finally { setBusyId(null); }
  }

  async function test(s: Subscription) {
    setBusyId(s.id);
    try {
      await notificationsApi.test(s.id);
      toast.success('Test sent. Check your inbox/phone.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Test failed.');
    } finally { setBusyId(null); }
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Channels</th>
            <th scope="col">Cadence</th>
            <th scope="col">Reminders</th>
            <th scope="col">Last delivered</th>
            <th scope="col">Sent</th>
            <th scope="col">Status</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {subs.map(s => (
            <Fragment key={s.id}>
              <tr>
                <td>{s.name}</td>
                <td>{s.channels.join(', ')}</td>
                <td>{s.cadence}</td>
                <td>{formatReminderOffsets(s)}</td>
                <td>{s.lastDeliveredAt ? new Date(s.lastDeliveredAt).toLocaleString() : '—'}</td>
                <td>{s.deliveryCount ?? 0}</td>
                <td>{s.isActive ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Paused</span>}</td>
                <td>
                  <div className="btn-group btn-group-sm" role="group">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => toggleActive(s)} disabled={busyId === s.id}>
                      {s.isActive ? 'Pause' : 'Resume'}
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => test(s)} disabled={busyId === s.id}>Send test</button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setOpenDeliveriesId(openDeliveriesId === s.id ? null : s.id)} disabled={busyId === s.id}>
                      {openDeliveriesId === s.id ? 'Hide' : 'Deliveries'}
                    </button>
                    <button type="button" className="btn btn-outline-danger" onClick={() => remove(s)} disabled={busyId === s.id}>Delete</button>
                  </div>
                </td>
              </tr>
              {openDeliveriesId === s.id ? (
                <tr>
                  <td colSpan={8} className="bg-light">
                    <DeliveriesList subId={s.id} />
                    {s.lastError ? <div className="text-danger small mt-2">Last error: {s.lastError}</div> : null}
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeliveriesList({ subId }: { subId: string }) {
  const [items, setItems] = useState<SubscriptionDelivery[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    notificationsApi.deliveries(subId, 25).then(setItems).catch(e => setErr(e?.message ?? 'Failed to load.'));
  }, [subId]);
  if (err) return <div className="text-danger small">{err}</div>;
  if (!items) return <div className="small"><Spinner label="Loading deliveries…" /></div>;
  if (items.length === 0) return <div className="small text-muted">No deliveries yet.</div>;
  return (
    <ul className="list-unstyled small mb-0">
      {items.map(d => (
        <li key={d.rowKey}>
          <strong>{new Date(d.attemptedAt).toLocaleString()}</strong> — {d.deliveryType} via {d.channelsCsv} ·{' '}
          <span className={
            d.status === 'sent' ? 'text-success' :
            d.status === 'failed' ? 'text-danger' :
            'text-muted'
          }>{d.status}</span>
          {d.errorMessage ? <span className="text-danger"> — {d.errorMessage}</span> : null}
        </li>
      ))}
    </ul>
  );
}
